import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { styled } from '@mui/material/styles';
import { useSettings } from '../context/SettingsContext';
import { useChatBot } from '../context/ChatBotContext';
import { formatTimeRange } from '../config/dates';
import { parseDateFromString } from '../lib/settings';

import FirstOfficerTrainingSelector from './FirstOfficerTrainingSelector';

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(3),
  color: '#0d2c54',
  fontFamily: "'Chillax', sans-serif",
  fontWeight: 500,
}));

export const DutyDetailsSection = ({ values, handleChange, setFieldValue, errors = {}, touched = {}, submitCount = 0, previousDutyType = null, isReturningMember = false, isUpdateOnly = false, freezeStatus = { frozen: false, frozenFrom: null, frozenUntil: null, fullFreeze: false } }) => {
  const availabilityFrozen = freezeStatus.fullFreeze;
  const { 
    getOpeningTimes, 
    getTheorySessions, 
    getPoolSessions, 
    getSeasonConfig,
    getSafetyOfficerDates,
    isFirstSafetyOfficerDeadlinePassed,
    isSecondSafetyOfficerDeadlinePassed,
    isLastTheorySessionPassed,
    isLastSecondOfficerPoolSessionPassed,
    isFirstOfficerTrainingDayPassed,
    isFirstOfficerTrainingAvailable
  } = useSettings();

  const { openWithMessage } = useChatBot();
  
  const theorySessionsPassed = isLastTheorySessionPassed();
  const poolSessionsPassed = isLastSecondOfficerPoolSessionPassed();
  // 2nd Safety Officer available if deadline not passed
  const is2ndOfficerAvailable = !isSecondSafetyOfficerDeadlinePassed();
  const trainingDayPassed = isFirstOfficerTrainingDayPassed();
  
  // Handle RLSS course question - if "no" and training day has passed, switch to 2nd officer or administrator
  const handleRLSSChange = (event) => {
    handleChange(event);
    // Only switch away if they said "no" AND training day has passed (they can't become a new 1st Safety Officer)
    if (event.target.value === "no" && trainingDayPassed && setFieldValue) {
      // Switch to 2nd Safety Officer if available, otherwise Administrator
      if (is2ndOfficerAvailable) {
        setFieldValue("dutyType", "2nd Safety Officer");
      } else {
        setFieldValue("dutyType", "Administrator");
      }
    }
  };

  const handleDutyTypeHelp = () => {
    let question;
    
    if (isReturningMember && previousDutyType) {
      if (previousDutyType === 'Administrator') {
        question = `I was an Administrator last year. Can you explain the different duty types and tell me about becoming a 2nd Safety Officer this year? What training would I need and what are the benefits of stepping up to a safety role?`;
      } else if (previousDutyType === '2nd Safety Officer') {
        question = `I was a 2nd Safety Officer last year. Can you explain the different duty types and tell me about becoming a 1st Safety Officer this year? What additional training or qualifications would I need, and what are the benefits (like the membership discount)?`;
      } else if (previousDutyType === '1st Safety Officer') {
        question = `I was a 1st Safety Officer last year. Can you explain the different duty types? I'd like to know about continuing as a 1st Safety Officer and the benefits that come with this role.`;
      } else {
        question = `I'm a returning member. Can you explain the different duty types (Administrator, 1st Safety Officer, 2nd Safety Officer) and what each role involves? Which would you recommend for me?`;
      }
    } else {
      question = `I'm a new member. Can you explain the different duty types (Administrator, 1st Safety Officer, 2nd Safety Officer) and what each role involves? Which would you recommend for someone just joining the club?`;
    }
    
    openWithMessage(question);
  };
  
  const OPENING_TIMES = getOpeningTimes();
  const SEASON_CONFIG = getSeasonConfig();
  const SAFETY_OFFICER_DATES = getSafetyOfficerDates();
  const theorySessions = getTheorySessions();
  const poolSessions = getPoolSessions();
  
  // Helper to check if a session date has passed
  const isSessionPassed = (sessionDate) => {
    const sessionDateObj = parseDateFromString(sessionDate);
    if (!sessionDateObj) return true;
    return new Date() > sessionDateObj;
  };
  
  // Filter sessions to only show future ones when updating
  const availableTheorySessions = isUpdateOnly 
    ? theorySessions.filter(session => !isSessionPassed(session.DATE))
    : theorySessions;
  const availablePoolSessions = isUpdateOnly 
    ? poolSessions.filter(session => !isSessionPassed(session.DATE))
    : poolSessions;
  
  const eveningTimeRange = formatTimeRange(
    OPENING_TIMES.REGULAR.EVENING.START,
    OPENING_TIMES.REGULAR.EVENING.END
  );
  const lateSeasonTimeRange = formatTimeRange(
    OPENING_TIMES.REGULAR.EVENING.START,
    OPENING_TIMES.REGULAR.EVENING.LATE_SEASON_END
  );
  const weekendTimeRange = formatTimeRange(
    OPENING_TIMES.REGULAR.WEEKEND.START,
    OPENING_TIMES.REGULAR.WEEKEND.END
  );
  const holidayTimeRange = formatTimeRange(
    OPENING_TIMES.SCHOOL_HOLIDAYS.START,
    OPENING_TIMES.SCHOOL_HOLIDAYS.END
  );

  return (
    <FormSection>
      <SectionTitle variant="h5">Duty Details</SectionTitle>
      <Typography variant="body2" paragraph>
        {(isFirstSafetyOfficerDeadlinePassed() && !is2ndOfficerAvailable) ? (
          "All members will need to help out a couple of times a year as an administrator. " +
          "The registration period for safety officers has closed for this season."
        ) : (
          <>
            All members will need to help out a couple of times a year as 
            {!isFirstSafetyOfficerDeadlinePassed() && is2ndOfficerAvailable && " a safety officer or "}
            {!isFirstSafetyOfficerDeadlinePassed() && !is2ndOfficerAvailable && " a 1st safety officer or "}
            {isFirstSafetyOfficerDeadlinePassed() && is2ndOfficerAvailable && " a 2nd safety officer or "}
            an administrator.
            {!isFirstSafetyOfficerDeadlinePassed() && (
              " First safety officers receive an RLSS certified training day plus a pool session every two years and in return " +
              `will get a ${SAFETY_OFFICER_DATES?.FIRST_OFFICER?.DISCOUNT_PERCENTAGE || 50}% discount on the membership fee. `
            )}
            {is2ndOfficerAvailable && (
              theorySessionsPassed
                ? "2nd safety officers who have already completed their theory session this year can sign up for a pool session."
                : "2nd safety officers need to attend one theory session and one pool session."
            )}
          </>
        )}
      </Typography>

      <FormControl component="fieldset">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FormLabel component="legend" sx={{ mb: 0 }}>This year I/we will help out as a:</FormLabel>
          <IconButton
            onClick={handleDutyTypeHelp}
            size="small"
            sx={{
              p: 0,
              color: '#0d2c54',
              '&:hover': {
                color: '#1a4a8a',
              },
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <RadioGroup
          name="dutyType"
          value={values.dutyType}
          onChange={(e) => {
            handleChange(e);
            setFieldValue('theoryCompleted', null);
            setFieldValue('theorySession', '');
            setFieldValue('poolSession', '');
          }}
        >
          {(!isFirstSafetyOfficerDeadlinePassed() || (isUpdateOnly && values.dutyType === "1st Safety Officer")) && (
            <FormControlLabel
              value="1st Safety Officer"
              control={<Radio />}
              label="1st Safety Officer"
              disabled={isUpdateOnly || (values.hadPriorTraining === "no" && trainingDayPassed)}
            />
          )}
          {(is2ndOfficerAvailable || (isUpdateOnly && values.dutyType === "2nd Safety Officer")) && (
            <FormControlLabel
              value="2nd Safety Officer"
              control={<Radio />}
              label="2nd Safety Officer"
              disabled={isUpdateOnly}
            />
          )}
          <FormControlLabel 
            value="Administrator" 
            control={<Radio />} 
            label="Administrator"
            disabled={isUpdateOnly}
          />
        </RadioGroup>
        {isUpdateOnly && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
            Duty type cannot be changed when updating details. Please contact the club if you need to change your role.
          </Typography>
        )}
      </FormControl>

      {values.dutyType && (
        <Box mt={3}>
          {values.dutyType === "1st Safety Officer" && !isUpdateOnly && (
            <>
              <FormControl component="fieldset">
                <FormLabel component="legend">Did you complete our RLSS course in 2025 or 2026?</FormLabel>
                <RadioGroup
                  name="hadPriorTraining"
                  value={values.hadPriorTraining}
                  onChange={handleRLSSChange}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}
          
          {values.dutyType === "1st Safety Officer" && !isUpdateOnly && (values.hadPriorTraining === "yes" || (values.hadPriorTraining === "no" && !trainingDayPassed)) && (
            <FirstOfficerTrainingSelector 
              values={values}
              handleChange={handleChange}
              showTrainingDay={values.hadPriorTraining === "no"}
            />
          )}
          
          {values.dutyType === "1st Safety Officer" && isUpdateOnly && (
            <FirstOfficerTrainingSelector 
              values={values}
              handleChange={handleChange}
              showTrainingDay={false}
              isUpdateOnly={true}
            />
          )}
          
          {values.hadPriorTraining === "no" && trainingDayPassed && values.dutyType !== "1st Safety Officer" && !isUpdateOnly && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: '8px', borderLeft: '4px solid #1976d2' }}>
              <Typography variant="body2" sx={{ color: '#1565c0' }}>
                Thank you for your interest in helping as a 1st Safety Officer, unfortunately there are no more RLSS training sessions.
              </Typography>
            </Box>
          )}

          {values.dutyType === "2nd Safety Officer" && (
            <>
              {theorySessionsPassed && !isUpdateOnly && !poolSessionsPassed ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Have you completed the 2nd Safety Officer theory course in {SEASON_CONFIG.REGISTRATION_YEAR}?
                  </Typography>
                  <RadioGroup
                    name="theoryCompleted"
                    value={values.theoryCompleted === true ? 'yes' : values.theoryCompleted === false ? 'no' : values.theoryCompleted === null ? '' : ''}
                    onChange={(e) => {
                      const completed = e.target.value === 'yes';
                      setFieldValue('theoryCompleted', completed);
                      setFieldValue('theorySession', '');
                      setFieldValue('poolSession', '');
                    }}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  {values.theoryCompleted === false && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: '8px', borderLeft: '4px solid #1976d2' }}>
                      <Typography variant="body2" sx={{ color: '#1565c0' }}>
                        Unfortunately you need to have completed the theory course before you can register as a 2nd Safety Officer. There are no more theory sessions available. Please select a different duty type.
                      </Typography>
                    </Box>
                  )}
                  {values.theoryCompleted === true && (
                    <Box sx={{ mt: 2 }}>
                      {availablePoolSessions.length > 0 ? (
                        <FormControl component="fieldset">
                          <FormLabel component="legend">
                            Please select which pool session you will attend:
                          </FormLabel>
                          <RadioGroup
                            name="poolSession"
                            value={values.poolSession}
                            onChange={handleChange}
                          >
                            {availablePoolSessions.map((session) => (
                              <FormControlLabel
                                key={session.DATE}
                                value={`${session.DATE} at ${session.TIME}`}
                                control={<Radio />}
                                label={`${session.DATE} at ${session.TIME}`}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      ) : null}
                      {submitCount > 0 && errors.poolSession && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>{errors.poolSession}</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    {availableTheorySessions.length > 0 ? (
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          {isUpdateOnly ? 'Change your theory session (only future sessions shown):' : 'Please select which theory session you will attend:'}
                        </FormLabel>
                        <RadioGroup
                          name="theorySession"
                          value={values.theorySession}
                          onChange={handleChange}
                        >
                          {availableTheorySessions.map((session) => (
                            <FormControlLabel
                              key={session.DATE}
                              value={`${session.DATE} at ${session.TIME}`}
                              control={<Radio />}
                              label={`${session.DATE} at ${session.TIME}`}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    ) : isUpdateOnly && values.theorySession ? (
                      <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                        <Typography variant="body2" color="textSecondary">
                          Your selected theory session: <strong>{values.theorySession}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                          This session has passed and cannot be changed.
                        </Typography>
                      </Paper>
                    ) : null}
                    {submitCount > 0 && errors.theorySession && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>{errors.theorySession}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {availablePoolSessions.length > 0 ? (
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          {isUpdateOnly ? 'Change your pool session (only future sessions shown):' : 'Please select which pool session you will attend:'}
                        </FormLabel>
                        <RadioGroup
                          name="poolSession"
                          value={values.poolSession}
                          onChange={handleChange}
                        >
                          {availablePoolSessions.map((session) => (
                            <FormControlLabel
                              key={session.DATE}
                              value={`${session.DATE} at ${session.TIME}`}
                              control={<Radio />}
                              label={`${session.DATE} at ${session.TIME}`}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    ) : isUpdateOnly && values.poolSession ? (
                      <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                        <Typography variant="body2" color="textSecondary">
                          Your selected pool session: <strong>{values.poolSession}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                          This session has passed and cannot be changed.
                        </Typography>
                      </Paper>
                    ) : null}
                    {submitCount > 0 && errors.poolSession && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>{errors.poolSession}</Typography>
                    )}
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {freezeStatus.frozen && (
            <Box sx={{ mt: 2, mb: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: '8px', borderLeft: '4px solid #e65100' }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                {freezeStatus.fullFreeze ? 'Duty availability is locked for the rest of the season' : 'Some duty availability dates are currently locked'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                {freezeStatus.fullFreeze 
                  ? <>The rota has been generated and your availability cannot be changed for the rest of the season. Please contact <a href="mailto:contact@comberton-pool.org.uk" style={{ color: '#0d2c54' }}>contact@comberton-pool.org.uk</a> if you need to make changes.</>
                  : <>The rota has been generated for dates up to {freezeStatus.frozenUntil ? freezeStatus.frozenUntil.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'the end of the season'}. You cannot change your unavailable dates within this period, but you can still update your availability for dates after this. Please contact <a href="mailto:contact@comberton-pool.org.uk" style={{ color: '#0d2c54' }}>contact@comberton-pool.org.uk</a> if you need to make changes to the locked period.</>
                }
              </Typography>
            </Box>
          )}

          <Box mt={3} sx={availabilityFrozen ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
            <FormLabel component="legend">
              On which days you can help out as a{values.dutyType === "Administrator" && "n"} {values.dutyType}?
            </FormLabel>
            <FormGroup>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.dutyMon}
                        onChange={handleChange}
                        name="dutyMon"
                        disabled={availabilityFrozen}
                      />
                    }
                    label={`Monday ${eveningTimeRange}`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.dutyTue}
                        onChange={handleChange}
                        name="dutyTue"
                        disabled={availabilityFrozen}
                      />
                    }
                    label={`Tuesday (Lane swimming) ${eveningTimeRange}`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.dutyWed}
                        onChange={handleChange}
                        name="dutyWed"
                        disabled={availabilityFrozen}
                      />
                    }
                    label={`Wednesday ${eveningTimeRange}`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.dutyFri}
                        onChange={handleChange}
                        name="dutyFri"
                        disabled={availabilityFrozen}
                      />
                    }
                    label={`Friday ${eveningTimeRange}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.dutySat}
                        onChange={handleChange}
                        name="dutySat"
                        disabled={availabilityFrozen}
                      />
                    }
                    label={`Saturday ${weekendTimeRange}`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.dutySun}
                        onChange={handleChange}
                        name="dutySun"
                        disabled={availabilityFrozen}
                      />
                    }
                    label={`Sunday ${weekendTimeRange}`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.dutyHol1}
                        onChange={handleChange}
                        name="dutyHol1"
                        disabled={availabilityFrozen}
                      />
                    }
                    label={`School holidays Tuesday ${holidayTimeRange}`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.dutyHol2}
                        onChange={handleChange}
                        name="dutyHol2"
                        disabled={availabilityFrozen}
                      />
                    }
                    label={`School holidays Thursday ${holidayTimeRange}`}
                  />
                </Grid>
              </Grid>
            </FormGroup>
            {errors.dutyWeekdays && (
              <Box sx={{ 
                mt: 1.5, 
                p: 1.5, 
                backgroundColor: '#F2F4CC', 
                borderRadius: '8px',
                borderLeft: '4px solid #0d2c54'
              }}>
                <Typography variant="body2" sx={{ color: '#0d2c54' }}>
                  Please select at least one day (weekday or weekend) for duties
                </Typography>
              </Box>
            )}
            <FormHelperText>
              Please note that from {SEASON_CONFIG.EVENING_TIMES_CHANGE_DATE} the evening opening times change to {lateSeasonTimeRange}.
            </FormHelperText>
          </Box>
        </Box>
      )}
    </FormSection>
  );
};

export default DutyDetailsSection;